import * as React from "react"

import { cva } from "../cva.ts"
import { useLayoutEffect } from "../hooks.ts"
import { withClass } from "../to-wrapped-children.ts"
import { useFieldRefs, type Field } from "./field.tsx"
import { useValidityRef } from "./validity.tsx"

export const Textbox = <T extends Textbox.Type = "text">({
  type,
  validate,
  ref: refProp,
  ...props
}: Textbox.Props<T>) => {
  const field = useFieldRefs()
  const ref = useValidityRef(refProp, validate)

  useTextareaResizeHandler(ref, type)

  return React.createElement(type === "textarea" ? type : "input", {
    name: field.name,
    id: field.uuid("control"),
    required: field.required,
    type: type === "textarea" ? undefined : type || "text",
    "aria-invalid": field.validityState === "valid" ? undefined : "true",
    "aria-labelledby": field.uuid("label"),
    "aria-describedby": [field.uuid("validity"), field.uuid("description")].join(" "),
    ref,
    ...withClass(props as React.ComponentProps<"textarea" | "input">, classForTextbox),
  })
}

const useTextareaResizeHandler = (ref: React.RefObject<HTMLElement | null>, type?: Textbox.Type) =>
  useLayoutEffect(() => {
    ref.current?.removeEventListener("input", handleResizeOnInput)

    if (type === "textarea") {
      ref.current?.addEventListener("input", handleResizeOnInput)
    }
  }, [type])

const handleResizeOnInput = ({ target }: any) => {
  target.style.height = "auto"
  target.style.height = target.scrollHeight + "px"
}

export namespace Textbox {
  export type Type =
    | "text"
    | "password"
    | "email"
    | "number"
    | "tel"
    | "search"
    | "url"
    | "textarea"
    | "datetime-local"

  export type Element<T extends Type> = T extends "textarea"
    ? HTMLTextAreaElement
    : HTMLInputElement

  export type Props<T extends Type = "text"> = {
    /** Specifies the type of text control to render. */
    type?: T

    /** Allows access to the underlying DOM element. */
    ref?: React.ForwardedRef<Element<T>>

    /** Validates the control's value. */
    validate?: Field.Validate<Element<T>>
  } & Omit<
    T extends "textarea"
      ? React.TextareaHTMLAttributes<HTMLTextAreaElement>
      : React.InputHTMLAttributes<HTMLInputElement>,
    "type"
  >

  export type Validate = <T extends Type>(control: Element<T>) => boolean
}

export const classForTextbox = cva([
  "w-stretch px-3 py-2",
  "text-sm",
  "bg-background text-gray-12",
  "bshadow bshadow-gray-4 rounded-sm",
  "transition-all",

  // &::placeholder
  "placeholder:text-gray-10",

  // &:focus-visible
  "focus-visible:bshadow-gray-8",
  "focus-visible:[outline:2px_solid_var(--gray-4)]",

  // &:disabled
  "disabled:cursor-not-allowed disabled:placeholder:text-gray-8 disabled:bshadow-gray-4",

  // &:invalid
  "user-invalid:bshadow-tomato-11 user-invalid:focus-visible:outline-tomato-4",
  "[&[aria-invalid='true']]:bshadow-tomato-11 [&[aria-invalid='true']]:focus-visible:outline-tomato-4",

  // &:is(textarea)
  "[&:is(textarea)]:min-h-[3.75rem] resize-none",
])
